import i18next, { LanguageDetectorAsyncModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as Localization from 'expo-localization';

import LocalStorageService from './LocalStorageService';
import * as locales from '../locales';
import { LanguageCodesEnum } from '@constants';

const LANGUAGE_STORAGE_KEY = 'language';

type LocaleType = Localization.Locale & {
  languageCode: string;
};

class LocalizationService {
  private _getLocales(): LocaleType[] {
    return Localization.getLocales().map((locale) => {
      // if we do not have a language code, we will get it from the language tag
      const languageCode =
        locale.languageCode ?? locale.languageTag.split('-')[0];

      return {
        ...locale,
        languageCode,
      };
    });
  }

  private async _detectLanguage(): Promise<string> {
    const value = await LocalStorageService.getItem(
      LANGUAGE_STORAGE_KEY,
    );

    // check if we already have selected language
    if (value) {
      return value;
    }

    // get the list of user preferred locales, in order.
    const deviceLocales = this._getLocales();

    // find the most preferred locale among those which we are supporting
    const possiblePreferredLocale = deviceLocales.find(
      ({ languageCode }) =>
        (Object.values(LanguageCodesEnum) as string[]).includes(
          languageCode,
        ),
    );

    // if we are not supporting any of preferred locales then return default "EN" locale
    return (
      possiblePreferredLocale?.languageCode || LanguageCodesEnum.En
    );
  }

  init() {
    const languageDetector: LanguageDetectorAsyncModule = {
      type: 'languageDetector',
      async: true,
      detect: (cb) => {
        this._detectLanguage().then(cb);
      },
      init: () => {},
      cacheUserLanguage: (language) => {
        LocalStorageService.setItem(LANGUAGE_STORAGE_KEY, language);
      },
    };

    return i18next
      .use(initReactI18next)
      .use(languageDetector)
      .init({
        compatibilityJSON: 'v3',
        fallbackLng: LanguageCodesEnum.En,
        resources: {
          [LanguageCodesEnum.En]: {
            translation: locales.en,
          },
          [LanguageCodesEnum.Fr]: {
            translation: locales.fr,
          },
          [LanguageCodesEnum.De]: {
            translation: locales.de,
          },
          [LanguageCodesEnum.It]: {
            translation: locales.it,
          },
          [LanguageCodesEnum.Pt]: {
            translation: locales.pt,
          },
          [LanguageCodesEnum.Es]: {
            translation: locales.es,
          },
        },
        react: {
          useSuspense: false,
        },
      });
  }

  get language() {
    return i18next.language as LanguageCodesEnum;
  }

  changeLanguage(newLanguage: LanguageCodesEnum) {
    return i18next.changeLanguage(newLanguage);
  }
}

export default new LocalizationService();
